import { Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { CurrentUserStore } from '@app/user/store/current-user/current-user.store';
import { SubscriptionPlan } from '@app/core/model/user/subscription-plan';
import { ISubscription } from '@app/core/model/subscription';
import { CodeFeaturesService } from './code-features.service';
import { BaseService } from '../base.service';
import { IUser } from '@app/core/model/user';
import { BehaviorSubject } from 'rxjs';

// Interface for the service.
export interface IUserSubscriptionService {
  isBasicUser(): boolean;
  isFreeTrialUser(): boolean;
  isIcsFreeTrialUser(): boolean;
  isNewUserWithoutFreeTrial(): boolean;
  hasCommercialSubscription(): boolean;
  hasExpiredCommercialSubscription(): boolean;
  isFreeTrialOrSubscriber(): boolean;
  isNewUserWithNoFreeTrialOrSubscription(): boolean;
  isActiveFreeTrialLikeUser(): boolean;
  hasExhaustedFreeTrialCredits(): boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserSubscriptionService extends BaseService implements IUserSubscriptionService {

  private readonly COMMERCIAL_PLANS = [SubscriptionPlan.PROFESSIONAL,
                                       SubscriptionPlan.ENTERPRISE,
                                       SubscriptionPlan.ICA,
                                       SubscriptionPlan.ICS_PAY_AS_YOU_GO];

  private subscription: ISubscription;
  private currentUser: IUser;

  private stateChangedSubject = new BehaviorSubject<boolean>(false);
  readonly stateChanged$ = this.stateChangedSubject.asObservable();

  constructor(private userStore: CurrentUserStore, private codeFeaturesService: CodeFeaturesService) {
    super();
    this.init();
  }

  private init(): void {
    this.subs.sink = this.userStore.stateChanged.pipe(filter(state => state != null && state.currentUser != null))
    .subscribe({
      next: state => {
        this.subscription = state.currentUser.Subscription;
        this.currentUser = state.currentUser;
        this.stateChangedSubject.next(true);
      }
    });
  }

  private isActiveSubscription(): boolean {
    return this.subscription && this.subscription.IsActive;
  }

  public isBasicUser(): boolean {
    return !this.isActiveSubscription() &&
           this.codeFeaturesService.freeTrialIsEnabled;
  }

  public isFreeTrialUser(): boolean {
    return this.isActiveSubscription() &&
           this.subscription.Plan === SubscriptionPlan.FREE_TRIAL &&
           this.codeFeaturesService.freeTrialIsEnabled;
  }

  public isIcsFreeTrialUser(): boolean {
    // ICS Free Trial is provisioned by PS/Portico, so plan recognition remains
    // independent of the legacy BSSH FreeTrial feature.
    return this.isActiveSubscription() &&
           this.subscription.Plan === SubscriptionPlan.ICS_FREE_TRIAL;
  }

  public isActiveFreeTrialLikeUser(): boolean {
    // Preserve legacy FreeTrial gating on the FreeTrial code feature, while allowing
    // active ICS Free Trial subscriptions independent of the legacy BSSH trial flow.
    // This method only classifies plan/state; action-level gates may separately
    // preserve an existing FreeTrial feature bypass, as app launch does.
    return this.isActiveSubscription() &&
           SubscriptionPlan.isFreeTrialLike(this.subscription.Plan) &&
           (this.subscription.Plan === SubscriptionPlan.ICS_FREE_TRIAL ||
            this.codeFeaturesService.freeTrialIsEnabled);
  }

  // TODO: How do we know this? Checking for inactive subscription is sufficient?
  // The implementation for this in the old code is exaclty same as isFreeTrialUser
  // https://git.illumina.com/BaseSpace/basespace/blob/5712d5c40188f1a071add5374afd86fa5c00fc22/src/Tang.Modules/User/subscriptionPlanService.ts#L26
  public isNewUserWithoutFreeTrial(): boolean {
    return this.subscription && !this.subscription.IsActive &&
           this.subscription.Plan === SubscriptionPlan.FREE_TRIAL &&
           this.codeFeaturesService.freeTrialIsEnabled;
  }

  public hasCommercialSubscription(): boolean {
    // Preserve the existing feature gate for legacy commercial plans. ICS PAYG
    // is provisioned independently and is treated as paid by the backend predicate.
    return this.isActiveSubscription() &&
           this.COMMERCIAL_PLANS.includes(this.subscription.Plan) &&
           (this.subscription.Plan === SubscriptionPlan.ICS_PAY_AS_YOU_GO ||
            this.codeFeaturesService.freeTrialIsEnabled);
  }

  public hasExpiredCommercialSubscription(): boolean {
    // Expiration changes subscription state, not how the plan is provisioned;
    // preserve the same legacy/ICS feature split used by the active predicate.
    return this.subscription && !this.subscription.IsActive &&
           this.COMMERCIAL_PLANS.includes(this.subscription.Plan) &&
           (this.subscription.Plan === SubscriptionPlan.ICS_PAY_AS_YOU_GO ||
            this.codeFeaturesService.freeTrialIsEnabled);
  }

  public isFreeTrialOrSubscriber(): boolean {
    return this.isFreeTrialUser() || this.isIcsFreeTrialUser() || this.hasCommercialSubscription();
  }

  public isNewUserWithNoFreeTrialOrSubscription(): boolean {
    return this.codeFeaturesService.freeTrialIsEnabled &&
         this.codeFeaturesService.freeTrialTemporarilyDisabled &&
         this.subscription && this.subscription.Plan === SubscriptionPlan.FREE_TRIAL &&
         !this.subscription.IsActive && !this.subscription.StartDate;
  }

  public hasActiveIcaSubscription(): boolean {
    return this.isActiveSubscription() && this.subscription.HasActiveIcaSubscription;
  }

  public hasExhaustedFreeTrialCredits(): boolean {
    // This is only the wallet/plan/credit signal. The hasWallet code feature mirrors
    // backend StoreLogic.HasWallet and preserves the legacy China/no-wallet behavior.
    // Action gates should combine this with active free-trial-like state so expired
    // trials are handled by subscription state.
    return !!(this.subscription &&
           SubscriptionPlan.isFreeTrialLike(this.subscription.Plan) &&
           this.codeFeaturesService.hasWallet &&
           this.subscription.Wallet &&
           this.subscription.Wallet.ICreditBalance <= 0);
  }

  public isWorkgroup(): boolean {
    return this.currentUser.IsWorkgroup;
  }
}
